import React, { useState } from "react";
import { Button, message, Spin } from "antd";
import { WizardProgress } from "../components/WizardProgress";
import { MappingWizardStep2 } from "./MappingWizardStep2";
import { MappingWizardStep3 } from "./MappingWizardStep3";
import { MappingWizardStep4 } from "./MappingWizardStep4";
import { MappingComplete } from "../components/MappingComplete";
import { MappingChange, MappingItem, PropertyGroups, TenantMapping } from "../Mapping";
import { getExternalMerchants } from "Services/Api/PciCompliance/Mapping.Service";
import { useTranslation } from "react-i18next";

interface CreateMappingContainerProps {
  tenantInfo: TenantMapping;
  handleBackToSettings: () => void;
  handleBackToHome: () => void;
  mappings: MappingItem[];
  mappingChanges: MappingChange[];
  venzaProperties: PropertyGroups[],
}

const WIZARD_STEPS = [
  { number: 1, title: "Upload Hierarchy" },
  { number: 2, title: "Review Mapping" },
  { number: 3, title: "Confirm & Save" },
];

export const CreateMappingContainer: React.FC<CreateMappingContainerProps> = ({
  tenantInfo,
  handleBackToSettings,
  handleBackToHome,
  venzaProperties,
}) => {
  const [mappingCount, setMappingCount] = useState<number>(0);
  const [currentStep, setCurrentStep] = useState<number>(1);
  const [passData, setPassData] = useState<MappingItem[]>([])
  const [excelData, setExcelData] = useState<MappingItem[]>([])
  const [fileName, setFileName] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { t } = useTranslation();

  const handleStep1Next = (uploadedFile: File, excel: MappingItem[]) => {
    setFileName(uploadedFile.name)
    setCurrentStep(2);
    setExcelData(excel.map(el => { return { ...el, venzaPropertyId: venzaProperties.find(e => e.name === el.venzaProperty)?.id ?? "", venzaProperty: venzaProperties.find(e => e.name === el.venzaProperty)?.name ?? "", venzaPropertyError: !!venzaProperties.find(e => e.name === el.venzaProperty) ? "" : `${el.venzaProperty} Property not found in Venza Property Portal`, encytroOffice: !!el.encytroMid ? el.encytroOffice : "" } }));
  };

  const handleStep2Next = async (changes: MappingItem[]) => {
    const mids = changes
      .filter(el => !!el.encytroMid && el?.status !== 'removed')
      .map(el => el.encytroMid);

    if (mids.length === 0) {
      message.warning(t("mapping.errors.selectTenant"));
      return;
    }

    const result = await getExternalMerchants(
      Number(tenantInfo.id),
      Number(tenantInfo.providerId),
      "",
      mids.join(",")
    );

    const resultMap = new Map<string, any>();
    (result ?? []).forEach((r: any) => {
      resultMap.set(String(r.mid), r);
    });
    const updatedChanges: MappingItem[] = changes.map(item => {
      if (!item.encytroMid) return item;

      const apiRow = resultMap.get(String(item.encytroMid));

      if (apiRow && apiRow.mapping_status !== "unmapped") {
        return {
          ...item,
          encytroOfficeError: t("mapping.errors.alreadyMapped"),
        };
      }

      return {
        ...item,
        encytroOfficeError: undefined,
      };
    });

    const hasErrors = updatedChanges.some(
      item => !!item.encytroOfficeError
    );

    if (hasErrors) {
      message.warning(t("mapping.errors.alreadyMapped2"));
      setPassData(updatedChanges);
      return;
    }

    setPassData(changes.filter(el => !el?.id?.startsWith("add")));
    setCurrentStep(3);
  };


  const handleConfirm = (count: number) => {
    setCurrentStep(4);
    setMappingCount(count);
  };

  const resetSettings = () => {
    setCurrentStep(1);
    setPassData([]);
  }

  if (currentStep === 4) {
    return (
      <div >
        <div className="d-flex justify-end mbx-16">
          <Button
            type="text"
            className="backToMappingSettings"
            onClick={handleBackToSettings}
          >
            {t('mapping.table.backToMapSettings')}
          </Button>
        </div>

        <MappingComplete
          tenantInfo={tenantInfo}
          mappingCount={mappingCount}
          onBackToHome={handleBackToHome}
        />
      </div>
    );
  }

  return (
    <Spin spinning={isSubmitting}>
      <div className="mw-896 mx-auto">
        <div className="d-flex justify-end mbx-16">
          <Button
            type="text"
            className="backToMappingSettings"
            onClick={handleBackToSettings}
          >
            {t('mapping.table.backToMapSettings')}
          </Button>
        </div>

        <div
          className="mb-8"
        >
          <h1 className="fs-15 card-foreground">
            {t('mapping.createMapping.title')}
          </h1>
          <p
            className="text-muted-foreground fs-1 fw-400 mb-0 mt-05"
          >
            {t('mapping.createMapping.subTitle')}
          </p>
        </div>

        <WizardProgress currentStep={currentStep} steps={WIZARD_STEPS.map((item) => ({...item, title: t(`mapping.createMapping.steps.${item.number}`)}))} />

        {currentStep === 1 && (
          <MappingWizardStep2
            tenantInfo={tenantInfo}
            onNext={handleStep1Next}
            onBack={handleBackToSettings}
          />
        )}
        {currentStep === 2 && (
          <MappingWizardStep3
            venzaProperties={venzaProperties}
            tenantInfo={tenantInfo}
            fileName={fileName}
            importedData={passData.length > 0 ? passData : excelData}
            onNext={handleStep2Next}
            onBack={resetSettings}
          />
        )}

        {currentStep === 3 && (
          <MappingWizardStep4
            tenantInfo={tenantInfo}
            fileName={fileName}
            mappings={passData.map(m => ({
              ...m,
              venzaProperty: m.venzaProperty ?? "",
              venzaPropertyId: m.venzaPropertyId ?? "",
              encytroOffice: m.encytroOffice ?? "",
              encytroMid: String(m.encytroMid ?? ""),
            }))}
            setIsSubmitting={setIsSubmitting}
            onConfirm={handleConfirm}
            onBack={() => setCurrentStep(2)}
          />
        )}
        {currentStep === 4 &&
          <MappingComplete
            tenantInfo={tenantInfo}
            mappingCount={mappingCount}
            onBackToHome={handleBackToHome}
          />
        }
      </div>
    </Spin>
  );
};

export default CreateMappingContainer;
